/**
 * Print Studio (v2) — Read-only React print renderer for ERPNext documents.
 * Clean, dedicated page for React-based print templates.
 * No Jinja/iframe mixing. Full control: live preview, customize panel, PDF download.
 * For creating documents from scratch, see DocumentCreatorPage.tsx.
 *
 * Uses PrintStudioShell for the shared layout (toolbar, preview area, sidebar).
 */

import React, { useEffect, useState, useCallback, useRef } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Printer, Download, Loader2, Settings2 } from "lucide-react";
import { getPrintData } from "@/lib/print/printReactService";
import { getPrintConfig } from "@/lib/print/printService";
import { logger } from "@/lib/utils/logger";
import type { PrintDocumentData, TemplateConfig } from "@zaviago/print-renderer";
import {
  InvoicePreview,
  QuotationPreview,
  TaxInvoicePreview,
  PackingSlipPreview,
  ShippingLabelPreview,
  DeliveryOrderPreview,
  TaxInvoiceAbbPreview,
  ExogroQuotationPreview,
  ExogroHeader,
  ExogroFooter,
  StandardHeader,
  CYNTaxInvoicePreview,
  CYNInvoicePreview,
  ZaviagoQuotationPreview,
  ZaviagoHeader,
  ZaviagoFooter,
  PaginatedPage,
  DocumentTemplatePage,
} from "@zaviago/print-renderer/preview";
import { useTemplateConfig } from "./useTemplateConfig";
import { TemplateSettingsPanel } from "./TemplateSettingsPanel";
import { PrintStudioShell } from "./PrintStudioShell";

type ReactFormat = {
  name: string;
  label: string;
  template_type: string;
};

/** Fallback: map doctype → default React template type when no Print Template records exist */
const DOCTYPE_TEMPLATE_MAP: Record<string, string> = {
  "Sales Order": "Invoice",
  "Sales Invoice": "TaxInvoice",
  "Quotation": "Quotation",
  "Delivery Note": "DeliveryOrder",
  "Purchase Order": "Invoice",
  "Purchase Invoice": "Invoice",
};

const PREVIEW_MAP: Record<
  string,
  React.ComponentType<{ data: PrintDocumentData; config?: Partial<TemplateConfig>; className?: string }>
> = {
  Invoice: InvoicePreview,
  Quotation: QuotationPreview,
  TaxInvoice: TaxInvoicePreview,
  PackingSlip: PackingSlipPreview,
  ShippingLabel: ShippingLabelPreview,
  DeliveryOrder: DeliveryOrderPreview,
  TaxInvoiceAbb: TaxInvoiceAbbPreview,
  ExogroQuotation: ExogroQuotationPreview,
  CYNTaxInvoice: CYNTaxInvoicePreview,
  CYNInvoice: CYNInvoicePreview,
  ZaviagoQuotation: ZaviagoQuotationPreview,
};

/** Maps template_type to a header component for repeat-header on page 2+. */
const HEADER_MAP: Record<string, React.ComponentType<{ data: PrintDocumentData; config?: Partial<TemplateConfig>; pageNumber?: number; totalPages?: number }>> = {
  ExogroQuotation: ExogroHeader,
  Quotation: StandardHeader,
  Invoice: StandardHeader,
  ZaviagoQuotation: ZaviagoHeader,
};

/** Maps template_type to a footer component for repeat-footer on every page. */
const FOOTER_MAP: Record<string, React.ComponentType<{ data: PrintDocumentData; config?: Partial<TemplateConfig> }>> = {
  ExogroQuotation: ExogroFooter,
  ZaviagoQuotation: ZaviagoFooter,
};

/** Strip the document's own signature section when quotation footer provides signatures */
function stripSignatureSection(html: string): string {
  return html.replace(/<h[2-4][^>]*>[^<]*signature[^<]*<\/h[2-4]>[\s\S]*$/i, '');
}

export function PrintStudioPage() {
  const { doctype = "", name = "" } = useParams<{ doctype: string; name: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedTemplate = searchParams.get("template") || "";

  // State
  const [reactFormats, setReactFormats] = useState<ReactFormat[]>([]);
  const [printData, setPrintData] = useState<PrintDocumentData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quotationPageCount, setQuotationPageCount] = useState(1);
  const printSourceRef = useRef<HTMLDivElement>(null);
  const [docPageCounts, setDocPageCounts] = useState<Record<number, number>>({});
  const totalDocPages = Object.values(docPageCounts).reduce((sum, c) => sum + c, 0);
  const [showCustomize, setShowCustomize] = useState(false);
  const [company, setCompany] = useState<string | null>(null);

  // Active template type — prefer Print Template record, fall back to doctype mapping
  const activeFormat = reactFormats.find((f) => f.name === selectedTemplate) || reactFormats[0];
  const templateType = activeFormat?.template_type || DOCTYPE_TEMPLATE_MAP[doctype] || "Invoice";
  const templateName = activeFormat?.name || null;

  // Template config hook
  const {
    config: templateConfig,
    updateConfig,
    saveConfig,
    resetConfig,
    isDirty,
    isSaving,
  } = useTemplateConfig(templateName);

  // Unsaved template config changes warning
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // Fetch company + React formats
  useEffect(() => {
    if (!doctype || !name) return;
    let stale = false;

    import("@/lib/api/tenantServerClient").then(({ tenantServer }) => {
      tenantServer
        .call<{ company?: string }>("frappe.client.get_value", {
          doctype,
          filters: { name },
          fieldname: "company",
        })
        .then((res) => {
          if (stale) return;
          const comp = res?.company || null;
          setCompany(comp);
          return getPrintConfig(doctype, comp || undefined);
        })
        .then((config) => {
          if (stale || !config) return;
          const allFormats = Array.isArray(config.formats) ? config.formats : [];
          const react = allFormats
            .filter((f: any) => f.renderer === "react" && f.template_type)
            .map((f: any) => ({
              name: f.name,
              label: f.label || f.name,
              template_type: f.template_type,
            }));
          setReactFormats(react);

          if (react.length > 0 && !searchParams.get("template")) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.set("template", react[0].name);
            setSearchParams(nextParams, { replace: true });
          }
        })
        .catch((err) => {
          if (!stale) logger.error("Failed to load print config:", err);
        });
    });

    return () => { stale = true; };
  }, [doctype, name]);

  // Fetch print data when doc changes
  useEffect(() => {
    if (!doctype || !name) return;
    let stale = false;
    setLoading(true);
    setError(null);

    getPrintData(doctype, name, company || undefined)
      .then((data) => {
        if (!stale) setPrintData(data);
      })
      .catch((err) => {
        if (!stale) {
          logger.error("Failed to load print data:", err);
          setError(err?.message ?? "Failed to load print data");
        }
      })
      .finally(() => {
        if (!stale) setLoading(false);
      });

    return () => { stale = true; };
  }, [doctype, name, company]);

  const handleTemplateChange = (newTemplate: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("template", newTemplate);
    setSearchParams(nextParams, { replace: true });
    setShowCustomize(false);
  };

  const handleDownload = useCallback(async () => {
    if (!printData) return;
    try {
      setLoading(true);
      setError(null);

      const { renderToStaticMarkup } = await import("react-dom/server");
      const { tenantServer } = await import("@/lib/api/tenantServerClient");

      const Component = PREVIEW_MAP[templateType];
      if (!Component) throw new Error(`Unknown template type: ${templateType}`);

      const FooterComp = FOOTER_MAP[templateType];
      const HeaderComp = HEADER_MAP[templateType];
      const hasRepeatHeader = !!(HeaderComp && (templateConfig.repeatHeader ?? false));
      const bodyHtml = renderToStaticMarkup(React.createElement(Component, { data: printData, config: templateConfig }));
      const footerHtml = (FooterComp && (templateConfig.showSignatures !== false))
        ? renderToStaticMarkup(React.createElement(FooterComp, { data: printData, config: templateConfig }))
        : '';
      // Repeat header on every printed page via <thead>
      const repeatHeaderHtml = hasRepeatHeader
        ? renderToStaticMarkup(React.createElement(HeaderComp, { data: printData, config: templateConfig }))
        : '';
      // Render header/footer HTML for attached docs (when docUseHeader/docUseFooter is enabled)
      const docHeaderHtml = (templateConfig.docUseHeader && HeaderComp)
        ? renderToStaticMarkup(React.createElement(HeaderComp, { data: printData, config: templateConfig }))
        : undefined;
      const docFooterHtml = (templateConfig.docUseFooter && FooterComp)
        ? renderToStaticMarkup(React.createElement(FooterComp, { data: printData, config: templateConfig }))
        : undefined;

      // Render attached document templates for PDF package
      // For print/PDF: use <table>/<tfoot> for sticky footer (flex doesn't work in Chrome print)
      let attachedDocsHtml = '';
      if (printData.attached_documents?.length) {
        const { DocumentTemplatePage: DocPage } = await import("@zaviago/print-renderer/preview");
        attachedDocsHtml = printData.attached_documents
          .map((doc) => {
            // Strip document's own signature section when quotation footer is used
            const contentHtml = docFooterHtml ? stripSignatureSection(doc.content_html) : doc.content_html;
            // Render the doc body (without footer — footer goes in tfoot for print)
            const docBodyHtml = renderToStaticMarkup(
              React.createElement(DocPage, {
                template_name: doc.template_name,
                category: doc.category,
                content_html: contentHtml,
                config: templateConfig,
                headerHtml: docHeaderHtml,
              })
            );
            // Wrap in table/tfoot if footer is needed (same pattern as main quotation)
            if (docFooterHtml) {
              return `<table style="width:100%;border-collapse:collapse;height:100%"><tfoot><tr><td style="vertical-align:bottom">${docFooterHtml}</td></tr></tfoot><tbody><tr><td style="vertical-align:top">${docBodyHtml}</td></tr></tbody></table>`;
            }
            return docBodyHtml;
          })
          .join('');
      }

      // When using <thead> repeat header, strip the inline header from bodyHtml
      // to prevent duplicate header on page 1 (Chrome repeats thead on ALL pages)
      let finalBodyHtml = bodyHtml;
      if (hasRepeatHeader) {
        const parser = new DOMParser();
        const parsed = parser.parseFromString(`<div>${bodyHtml}</div>`, 'text/html');
        const bodyRoot = parsed.querySelector('body > div > div');
        if (bodyRoot) {
          const inlineHeader = bodyRoot.firstElementChild;
          if (inlineHeader) inlineHeader.remove();
          const separator = bodyRoot.firstElementChild;
          if (separator && (separator as HTMLElement).style?.borderTop) separator.remove();
          finalBodyHtml = bodyRoot.outerHTML;
        }
      }

      // Table layout: <thead> repeats header, <tfoot> pins footer on every printed page.
      // Attached docs go OUTSIDE the table so page-break-before:always works correctly.
      const needsTable = footerHtml || repeatHeaderHtml;
      const theadHtml = repeatHeaderHtml
        ? `<thead><tr><td style="padding-bottom:12px">${repeatHeaderHtml}</td></tr></thead>`
        : '';
      // Footer dual-element: invisible tfoot reserves space, position:fixed div pins to paper bottom
      const tfootHtml = footerHtml
        ? `<tfoot><tr><td style="vertical-align:bottom;visibility:hidden">${footerHtml}</td></tr></tfoot>`
        : '';
      const fixedFooterHtml = footerHtml
        ? `<div class="print-fixed-footer">${footerHtml}</div>`
        : '';
      const mainHtml = needsTable
        ? `${fixedFooterHtml}<table style="width:100%;border-collapse:collapse;height:100%">${theadHtml}${tfootHtml}<tbody><tr><td style="vertical-align:top">${finalBodyHtml}</td></tr></tbody></table>`
        : finalBodyHtml;
      const html = mainHtml + attachedDocsHtml;

      const fullHtml = `<!DOCTYPE html>
<html style="height:100%;margin:0"><head>
<meta charset="utf-8">
<link href="https://fonts.googleapis.com/css2?family=Sarabun:ital,wght@0,400;0,700;1,400;1,700&display=swap" rel="stylesheet">
<style>
  @page { size: A4; margin: 0; }
  body { margin: 0; padding: 0; height: 100%; font-family: 'Sarabun', 'TH Sarabun New', sans-serif; background: #fff; }
  * { box-sizing: border-box; }
  tr, li, p, h1, h2, h3, h4, h5, h6 { break-inside: avoid; page-break-inside: avoid; }
  .print-fixed-footer { position: fixed; bottom: 0; left: 0; width: 100%; }
</style>
</head><body>${html}</body></html>`;

      const blob = await tenantServer.callBlob(
        "zaviago_backend.api.print.html_to_pdf",
        { html: fullHtml, filename: `${doctype}-${name}` },
      );

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${doctype}-${name}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      logger.error("Failed to download PDF:", err);
      setError(err instanceof Error ? err.message : "Failed to download PDF");
    } finally {
      setLoading(false);
    }
  }, [printData, templateType, templateConfig, doctype, name]);

  const handlePrint = useCallback(() => {
    if (!printData || !printSourceRef.current) return;

    // ── DOM-clone approach: grab the already-rendered preview pages ──
    // The React preview (PaginatedPage) already computed correct smart page breaks,
    // correct page numbers, and rendered with properly loaded fonts.
    // Instead of re-measuring (which failed 3 ways — iframe fonts not loaded,
    // main document CSS interference, iframe font polling), we clone the visible
    // page divs directly from the DOM.
    //
    // PaginatedPage renders: hidden measurers (aria-hidden) + visible page divs.
    // Each visible page div is 794x1123 with overflow:hidden — exactly A4 at 96 DPI.
    // We clone them, strip decorative styles, add page-break-after, and print.

    const pages: string[] = [];
    const collectPages = (container: Element) => {
      Array.from(container.children).forEach(child => {
        // Skip hidden measurer divs (PaginatedPage uses aria-hidden for them)
        if (child.getAttribute('aria-hidden') === 'true' || child.getAttribute('aria-hidden') === '') return;
        const el = child as HTMLElement;
        // Only collect page-sized divs (794px wide, 1123px tall)
        if (el.offsetWidth < 700 || el.offsetHeight < 1000) return;
        const clone = el.cloneNode(true) as HTMLElement;
        // Strip preview-only decorative styles
        clone.style.boxShadow = 'none';
        clone.style.borderRadius = '0';
        clone.style.marginBottom = '0';
        clone.style.marginTop = '0';
        // Use mm units for print accuracy
        clone.style.width = '210mm';
        clone.style.height = '297mm';
        clone.style.pageBreakAfter = 'always';
        pages.push(clone.outerHTML);
      });
    };

    // Collect all PaginatedPage containers inside the print source wrapper
    Array.from(printSourceRef.current.children).forEach(child => {
      collectPages(child);
    });

    if (pages.length === 0) return;

    // ── Post-process: fix page numbers to show correct global numbering ──
    // The preview renders each PaginatedPage with its own local page count.
    // Main quotation might show "1/3" while attached docs show "1/8".
    // Replace ALL page number patterns with correct global sequential numbers.
    const totalPages = pages.length;
    for (let pi = 0; pi < pages.length; pi++) {
      const globalPage = pi + 1;
      // Thai pattern: หน้า</span> followed by <span...>N / M</span>
      pages[pi] = pages[pi].replace(
        /(\u0e2b\u0e19\u0e49\u0e32<\/span>\s*<span[^>]*>)\s*\d+\s*\/\s*\d+\s*(<\/span>)/g,
        `$1${globalPage} / ${totalPages}$2`
      );
      // English pattern: >page N/N< (e.g. TaxInvoiceAbbPreview)
      pages[pi] = pages[pi].replace(
        /(>page\s+)\d+\/\d+(\s*<)/gi,
        `$1${globalPage}/${totalPages}$2`
      );
    }

    // Remove page-break-after from the very last page
    const lastPage = pages[pages.length - 1];
    pages[pages.length - 1] = lastPage.replace('page-break-after: always', 'page-break-after: auto');

    // Build print iframe
    const existing = document.getElementById("print-frame");
    if (existing) existing.remove();

    const iframe = document.createElement("iframe");
    iframe.id = "print-frame";
    iframe.style.position = "fixed";
    iframe.style.left = "-9999px";
    iframe.style.top = "0";
    iframe.style.width = "210mm";
    iframe.style.height = "auto";
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentDocument;
    if (!iframeDoc) return;

    const cssBlock = [
      '* { margin: 0; padding: 0; box-sizing: border-box; }',
      "body { font-family: 'Sarabun', 'TH Sarabun New', sans-serif; }",
      '@media print {',
      '  @page { size: A4; margin: 0; }',
      '  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }',
      '  tr, li, p, h1, h2, h3, h4, h5, h6 { break-inside: avoid; page-break-inside: avoid; }',
      '}',
    ].join('\n');

    iframeDoc.open();
    iframeDoc.write([
      '<!DOCTYPE html><html><head><meta charset="utf-8"/>',
      '<title>' + (doctype || '') + ' - ' + (name || '') + '</title>',
      '<link href="https://fonts.googleapis.com/css2?family=Sarabun:ital,wght@0,400;0,700;1,400;1,700&display=swap" rel="stylesheet">',
      '<style>' + cssBlock + '</style>',
      '</head><body>',
      pages.join(''),
      '</body></html>',
    ].join(''));
    iframeDoc.close();

    const win = iframe.contentWindow;
    if (!win) return;

    // Wait for font to load in print iframe, then print
    const waitAndPrint = () => {
      let attempts = 0;
      const check = () => {
        attempts++;
        if (win.document.fonts.check('13pt Sarabun') || attempts > 80) {
          // Every font the document uses is loaded, not just Sarabun (was a fixed 100ms).
          void win.document.fonts.ready.then(() => win.print());
        } else {
          setTimeout(check, 50);
        }
      };
      setTimeout(check, 150);
    };
    waitAndPrint();
  }, [printData, doctype, name]);

  if (!doctype || !name) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-sm text-muted-foreground">Invalid print parameters</div>
      </div>
    );
  }

  const PreviewComponent = PREVIEW_MAP[templateType];

  const centerSlot = reactFormats.length > 0 ? (
    <Select value={templateName || ""} onValueChange={handleTemplateChange}>
      <SelectTrigger className="h-8 w-[280px] rounded-full px-4 text-xs bg-card border border-border/40 shadow-sm">
        <SelectValue placeholder="Select template" />
      </SelectTrigger>
      <SelectContent>
        {reactFormats.map((fmt) => (
          <SelectItem key={fmt.name} value={fmt.name}>
            {fmt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  ) : (
    <span className="text-xs text-muted-foreground">
      {loading ? "Loading templates..." : "No React templates available"}
    </span>
  );

  const actionsSlot = (
    <>
      <Button
        variant={showCustomize ? "secondary" : "ghost"}
        size="sm"
        onClick={() => setShowCustomize((v) => !v)}
        className="h-7 text-xs rounded-full px-3 text-foreground"
      >
        <Settings2 className="mr-1.5 h-3.5 w-3.5" />
        Customize
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleDownload}
        disabled={loading || !printData}
        className="h-7 text-xs rounded-full px-3 text-foreground"
      >
        {loading ? (
          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
        ) : (
          <Download className="mr-1.5 h-3.5 w-3.5" />
        )}
        {printData?.attached_documents?.length ? `Download Package (${printData.attached_documents.length})` : "Download"}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={handlePrint}
        disabled={loading || !printData}
        className="h-7 text-xs rounded-full px-3 text-foreground"
      >
        <Printer className="mr-1.5 h-3.5 w-3.5" />
        Print
      </Button>
    </>
  );

  const sidebar = showCustomize ? (
    <TemplateSettingsPanel
      config={templateConfig}
      onChange={updateConfig}
      onSave={saveConfig}
      onReset={resetConfig}
      saving={isSaving}
      open={showCustomize}
      onOpenChange={setShowCustomize}
      dirty={isDirty}
    />
  ) : undefined;

  return (
    <PrintStudioShell
      backLabel={name}
      centerSlot={centerSlot}
      actionsSlot={actionsSlot}
      sidebar={sidebar}
    >
      {loading && (
        <div className="absolute left-4 top-4 z-10 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading preview...
        </div>
      )}
      {error && (
        <div className="absolute left-4 top-4 z-10 rounded-xs bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {printData && PreviewComponent ? (
        <div ref={printSourceRef}>
        <PaginatedPage
          className="mx-auto print-preview"
          style={{ width: 794 }}
          header={(() => {
            const HeaderComp = HEADER_MAP[templateType];
            if (!HeaderComp) return undefined;
            return (pageNumber: number, totalPages: number) =>
              <HeaderComp data={printData} config={templateConfig} pageNumber={pageNumber} totalPages={totalPages} />;
          })()}
          repeatHeader={templateConfig.repeatHeader ?? false}
          footer={(() => {
            const FooterComp = FOOTER_MAP[templateType];
            return FooterComp ? <FooterComp data={printData} config={templateConfig} /> : undefined;
          })()}
          repeatFooter={templateConfig.repeatFooter ?? false}
          onPageCount={setQuotationPageCount}
          extraPageCount={totalDocPages}
        >
          <PreviewComponent data={printData} config={templateConfig} />
        </PaginatedPage>
        {/* Attached document templates — each wrapped in PaginatedPage for multi-page support */}
        {printData.attached_documents?.map((doc, i) => {
          const HeaderComp = HEADER_MAP[templateType];
          const globalTotal = quotationPageCount + totalDocPages;
          // Pages before this doc = quotation pages + sum of previous docs' page counts
          const pagesBefore = quotationPageCount + Array.from({ length: i }, (_, k) => docPageCounts[k] ?? 1).reduce((s, c) => s + c, 0);
          // Chrome routing between PaginatedPage and DocumentTemplatePage:
          //
          // HEADER: Page 1 gets header via DocumentTemplatePage's headerHtml (inline in
          //   content — PaginatedPage doesn't render its header on page 1). Pages 2+ get
          //   header via PaginatedPage's header prop. The inline headerHtml is clipped away
          //   on pages 2+ by the negative top offset, so no duplication.
          //
          // FOOTER: PaginatedPage owns footer on ALL pages (absolute-positioned at bottom,
          //   space reserved in break calculation). NEVER pass footerHtml to
          //   DocumentTemplatePage — that would create a duplicate (inline + absolute) and
          //   confuse the page break algorithm.
          const wantFooter = !!(templateConfig.repeatFooter || templateConfig.docUseFooter);
          const wantHeader = !!(templateConfig.repeatHeader || templateConfig.docUseHeader);
          return (
            <PaginatedPage
              key={`doc-${i}`}
              className="mx-auto"
              style={{ width: 794, marginTop: 24 }}
              header={wantHeader && HeaderComp
                ? (pn: number, _tp: number) => <HeaderComp data={printData} config={templateConfig} pageNumber={pagesBefore + pn} totalPages={globalTotal} />
                : undefined}
              repeatHeader={wantHeader}
              footer={(() => {
                const FooterC = FOOTER_MAP[templateType];
                return (wantFooter && FooterC) ? <FooterC data={printData} config={templateConfig} /> : undefined;
              })()}
              repeatFooter={wantFooter}
              onPageCount={(count) => setDocPageCounts((prev) => prev[i] === count ? prev : { ...prev, [i]: count })}
            >
              <DocumentTemplatePage
                template_name={doc.template_name}
                category={doc.category}
                content_html={doc.content_html}
                config={templateConfig}
                style={{ pageBreakBefore: undefined }}
                headerHtml={templateConfig.docUseHeader && HeaderComp
                  ? renderToStaticMarkup(React.createElement(HeaderComp, { data: printData, config: templateConfig, pageNumber: pagesBefore + 1, totalPages: globalTotal }))
                  : undefined}
              />
            </PaginatedPage>
          );
        })}
        </div>
      ) : !loading && !error ? (
        <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
          Select a template to preview
        </div>
      ) : null}
    </PrintStudioShell>
  );
}
